import { useCallback, useEffect, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { type AiLimitsLoad, fetchAiLimits, saveAiCap } from './aiLimitsApi';

/**
 * GET /api/ai/limits on mount and whenever `retry` is called; and `save`, which
 * sets or clears the cap (PUT /api/ai/limits) and resolves once it has finished.
 * A failed load is the error state (so no warning); a failed save leaves the
 * figures as they were and `saveError` says why.
 */
export function useAiLimits(): {
  load: AiLimitsLoad;
  retry: () => void;
  save: (monthlyTokenCap: number | null) => Promise<void>;
  saving: boolean;
  saveError: string | null;
  clearSaveError: () => void;
} {
  const [load, setLoad] = useState<AiLimitsLoad>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoad({ state: 'loading' });
    fetchAiLimits(authenticatedFetch, { signal: controller.signal }).then(
      (body) => setLoad({ state: 'ready', body }),
      (error: unknown) => {
        if (!controller.signal.aborted) {
          setLoad({ state: 'error', message: error instanceof Error ? error.message : 'Could not load AI limits.' });
        }
      },
    );
    return () => controller.abort();
  }, [attempt]);

  const save = useCallback(async (monthlyTokenCap: number | null) => {
    setSaving(true);
    setSaveError(null);
    try {
      const body = await saveAiCap(authenticatedFetch, monthlyTokenCap);
      setLoad({ state: 'ready', body });
    } catch (error: unknown) {
      setSaveError(error instanceof Error ? error.message : 'Try again.');
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    load,
    retry: () => setAttempt((n) => n + 1),
    save,
    saving,
    saveError,
    clearSaveError: () => setSaveError(null),
  };
}
